import { OrgVersion } from "@superblocksteam/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useFeatureFlag } from "hooks/ui/useFeatureFlag";
import { Flag } from "store/slices/featureFlags/models/Flags";
import { selectOnlyOrganization } from "store/slices/organizations/selectors";
import logger from "utils/logger";
import type { OrgVersionType } from "@superblocksteam/shared";
import type { ReactNode } from "react";

const STORAGE_KEY = "superblocks-view-v2-enabled";

interface ViewSuperblocksV2ContextType {
  canShowToggle: boolean;
  // True if user is viewing the V2 experience (V2 org OR V1 org with toggle enabled)
  viewSuperblocksV2: boolean;
  setViewSuperblocksV2: (value: boolean) => void;
  effectiveOrgVersion: OrgVersionType;
  // True only for V1 orgs transitioning to V2 (toggle enabled) - NOT for native V2 orgs
  // Use this for V1-specific transition UI like "Import from Superblocks 1.0"
  isOrgV1ViewingV2Transition: boolean;
  togglePulseActive: boolean;
  triggerTogglePulse: () => void;
}

const ViewSuperblocksV2Context = createContext<
  ViewSuperblocksV2ContextType | undefined
>(undefined);

/**
 * Reads the user's explicit toggle preference from localStorage.
 * Returns null if the user has never toggled (no stored value).
 */
function readStoredPreference(): boolean | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) return null;
    return stored === "true";
  } catch {
    return null;
  }
}

export function ViewSuperblocksV2Provider({
  children,
}: {
  children: ReactNode;
}) {
  const organization = useSelector(selectOnlyOrganization);
  const isV1Org = organization?.version === OrgVersion.V1;
  const toggleFlagEnabled = useFeatureFlag(
    Flag.V1_ORG_ENABLE_SUPERBLOCKS_2_VIEW,
  );
  const defaultV2View = useFeatureFlag(Flag.V1_ORG_DEFAULT_SUPERBLOCKS_2_VIEW);

  // Track user's explicit toggle preference (null = never set)
  const [userPreference, setUserPreference] = useState<boolean | null>(
    readStoredPreference,
  );

  // Wrapper that saves to localStorage
  const setViewSuperblocksV2 = useCallback((value: boolean) => {
    setUserPreference(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch (error) {
      logger.warn(`Error setting localStorage key "${STORAGE_KEY}"`, { error });
    }
  }, []);

  const isV2Org = organization?.version === OrgVersion.V2;

  // Toggle UI only available for V1 orgs with toggle feature flag enabled
  const canShowToggle = isV1Org && toggleFlagEnabled;

  // Effective V2 view for V1 orgs:
  // - If toggle is available: use user's preference, falling back to default flag
  // - If toggle is not available: default flag determines the view
  const viewSuperblocksV2 = canShowToggle
    ? (userPreference ?? defaultV2View)
    : defaultV2View;

  // V1 org that is viewing V2 - transitioning to V2
  // Use this for V1-specific transition UI (toggle, "Import from Superblocks 1.0")
  const isOrgV1ViewingV2Transition = isV1Org && viewSuperblocksV2;

  // True if viewing V2 experience: either native V2 org OR V1 org with toggle enabled
  const isViewingV2 = isV2Org || isOrgV1ViewingV2Transition;

  // Pulse animation state for the V2 toggle
  const [togglePulseActive, setTogglePulseActive] = useState(false);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const triggerTogglePulse = useCallback(() => {
    setTogglePulseActive(true);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => setTogglePulseActive(false), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    };
  }, []);

  // Effective version for feature gating
  const effectiveOrgVersion: OrgVersionType = useMemo(() => {
    if (isOrgV1ViewingV2Transition) {
      return OrgVersion.V2;
    }
    return organization?.version ?? OrgVersion.V1;
  }, [isOrgV1ViewingV2Transition, organization?.version]);

  const value = useMemo(
    () => ({
      canShowToggle,
      viewSuperblocksV2: isViewingV2,
      setViewSuperblocksV2,
      effectiveOrgVersion,
      isOrgV1ViewingV2Transition,
      togglePulseActive,
      triggerTogglePulse,
    }),
    [
      canShowToggle,
      isViewingV2,
      setViewSuperblocksV2,
      effectiveOrgVersion,
      isOrgV1ViewingV2Transition,
      togglePulseActive,
      triggerTogglePulse,
    ],
  );

  return (
    <ViewSuperblocksV2Context.Provider value={value}>
      {children}
    </ViewSuperblocksV2Context.Provider>
  );
}

export function useViewSuperblocksV2Context(): ViewSuperblocksV2ContextType {
  const context = useContext(ViewSuperblocksV2Context);
  if (context === undefined) {
    // Fallback for components outside the provider (return safe defaults)
    return {
      canShowToggle: false,
      viewSuperblocksV2: false,
      setViewSuperblocksV2: () => {},
      effectiveOrgVersion: OrgVersion.V1,
      isOrgV1ViewingV2Transition: false,
      togglePulseActive: false,
      triggerTogglePulse: () => {},
    };
  }
  return context;
}
